import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import BrandMark from './BrandMark';
import AppIcon from './AppIcon';
import heroArtwork from '../assets/stockhunt-hero-v1.png';

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { user, register } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await register(username, email, password, firstName, lastName);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="auth-page auth-page--register">
      <div className="auth-shell">
        <aside className="auth-showcase">
          <button type="button" className="auth-brand" onClick={() => navigate('/')} aria-label="Back to StockHunt home">
            <span className="app-brand__mark"><BrandMark /></span>
            <span>StockHunt</span>
          </button>
          <div className="auth-showcase__copy">
            <span className="auth-eyebrow">Start with a clearer picture</span>
            <h1>Build a workspace around the wealth you already own.</h1>
            <p>Add your holdings, understand your actual returns, and follow your progress without spreadsheet sprawl.</p>
            <div className="auth-benefits">
              <span><AppIcon name="check" /> Stocks and mutual funds together</span>
              <span><AppIcon name="check" /> Retirement and physical assets included</span>
              <span><AppIcon name="check" /> Long-term analysis and projections</span>
            </div>
          </div>
          <img src={heroArtwork} alt="" className="auth-showcase__art" aria-hidden="true" />
        </aside>

        <section className="auth-form-panel" aria-labelledby="register-title">
          <div className="auth-form-card auth-form-card--wide">
            <span className="auth-form-card__kicker">Create your workspace</span>
            <h2 id="register-title">Create an account</h2>
            <p className="auth-form-card__intro">A few details are all you need to begin.</p>

            {error && <Alert variant="danger" className="auth-alert">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>First name</Form.Label>
                    <Form.Control type="text" placeholder="e.g. Dennis" value={firstName} onChange={(event) => setFirstName(event.target.value)} disabled={isLoading} autoComplete="given-name" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Last name</Form.Label>
                    <Form.Control type="text" placeholder="e.g. John" value={lastName} onChange={(event) => setLastName(event.target.value)} disabled={isLoading} autoComplete="family-name" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Username</Form.Label>
                    <Form.Control type="text" placeholder="Choose a username" value={username} onChange={(event) => setUsername(event.target.value)} required disabled={isLoading} autoComplete="username" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Email address</Form.Label>
                    <Form.Control type="email" placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={isLoading} autoComplete="email" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Password</Form.Label>
                    <Form.Control type="password" placeholder="Create a password" value={password} onChange={(event) => setPassword(event.target.value)} required disabled={isLoading} autoComplete="new-password" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Confirm password</Form.Label>
                    <Form.Control type="password" placeholder="Repeat password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required disabled={isLoading} autoComplete="new-password" />
                  </Form.Group>
                </Col>
              </Row>

              <Button variant="primary" type="submit" className="auth-submit mt-4" disabled={isLoading}>
                {isLoading ? <><Spinner animation="border" size="sm" /> Creating account…</> : 'Create my StockHunt account'}
              </Button>
            </Form>

            <p className="auth-switch">
              Already have an account?{' '}
              <button type="button" onClick={() => navigate('/login')}>Sign in</button>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Register;
